import React, { useCallback, useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  deleteUserProfileImage,
  getDetailsForUser,
  setUserProfileImage,
  updateUserAttribute,
} from "../../services/UserService";
import { UserContext } from "../MainLayout";
import EmptyScreen from "../../utils/components/common/EmptyScreen/EmptyScreen";
import Icons from "../../utils/components/icons";
import LoadingSpinner from "../../utils/components/common/loadingSpinner/LoadingSpinner";
import { REGEX } from "../../utils/constants/regex";
import PhoneInput from "react-phone-input-2";
import Modal from "../../utils/components/common/modal";
import OtpInput from "react-otp-input";
import {
  getUserAuthenticationHookDetails,
  sendOTP,
} from "../../services/AuthService";
import "../../pages/auth/Auth.scss";
import ModalComponent from "../../utils/components/common/modalComponent/ModalComponent";

const EditProfile = ({ hideEditProfileModal }) => {
  const { t } = useTranslation("header");
  const { userCognitoData, setUserGlobalData, userGlobalData } =
    useContext(UserContext);
  const [isLoading, setLoading] = useState(true);
  const [userData, setUserData] = useState(null);
  const [err, setErr] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [isEditEnabled, setIsEditEnabled] = useState(false);
  const [userName, setUserName] = useState(null);
  const [isMobileNoEditEnabled, setIsMobileNoEditEnabled] = useState(false);
  const [userMobNo, setUserMobNo] = useState("");
  const [otpVerifyError, setOTPVerifyError] = useState(false);
  const [otpVerifyErrorMessage, setOTPVerifyErrorMessage] = useState("");
  const [resendOTP, setResendOTP] = useState(false);
  const [verifyDisable, setVerifyDisable] = useState(true);
  const [isMobOTPModalActive, setIsMobOTPModalActive] = useState(false);
  const [otpEntered, setOtpEntered] = useState(null);
  const [isErrorModal, setIsErrorModal] = useState(false);
  const [isChangePicModalActive, setIsChangePicModalActive] = useState(false);
  const [validation, setValidation] = useState({
    phoneUsed: false,
  });
  const [file, setFile] = useState(null);
  const [isPicUploadSuccess, setIsPicUploadSuccess] = useState(false);

  const [debounceTimeout, setDebounceTimeout] = useState(null);
  const [actionSelected, setActionSelected] = useState("");
  const [previewUrl, setPreviewUrl] = useState(null);
  const [selectedFileFormat, setSelectedFileFormat] = useState("");

  const [isSaveClicked, setIsSaveClicked] = useState(false);

  const getUserProfileDetails = useCallback(async () => {
    setErr(false);
    try {
      const queryParam = {
        cognitoId:
          userCognitoData?.signInUserSession?.accessToken?.payload?.username,
      };
      const response = await getDetailsForUser(queryParam);
      setUserData(response?.data);
      setUserGlobalData(response?.data?.UserDetails);
      setLoading(false);
    } catch (err) {
      setErr(true);
      setLoading(false);
    }
  }, [userCognitoData, setUserGlobalData]);

  const updateAttributeOfUser = useCallback(async () => {
    try {
      const queryParam = {
        cognitoId:
          userCognitoData?.signInUserSession?.accessToken?.payload?.username,
        attributeName: "name",
        attributeValue: userName,
      };

      await updateUserAttribute(queryParam);

      const newUserDetails = {
        ...userGlobalData,
        UserName: userName,
      };

      setUserGlobalData(newUserDetails);
    } catch (err) {
      if (err.code !== "ERR_BAD_REQUEST") {
        setIsErrorModal(true);
        setUserName(userData?.UserDetails?.UserName);
      }
    }
  }, [userCognitoData, userName, userData, setUserGlobalData, userGlobalData]);

  useEffect(() => {
    if (userCognitoData) {
      getUserProfileDetails();
    }
  }, [getUserProfileDetails, userCognitoData]);

  useEffect(() => {
    setUserName(userData?.UserDetails?.UserName);
    setUserMobNo(userData?.UserDetails?.PhoneNumber);
  }, [userData]);

  const handleUserNameEdit = useCallback((e) => {
    const tempName = e.target.value;

    setUserName(tempName);

    setErrorMsg("");
  }, []);

  const userNameErrorCheck = useCallback(() => {
    let err1 = t("editProfile.inValidName");

    if (!REGEX.nameReg.test(userName)) {
      setErrorMsg(err1);
      return;
    } else {
      setIsEditEnabled(false);
      setIsSaveClicked(true);
    }
  }, [t, userName]);

  useEffect(() => {
    if (isSaveClicked) {
      if (userData) {
        updateAttributeOfUser();

        if (!isErrorModal) {
          setUserData((userData) => {
            return {
              ...userData,
              UserDetails: {
                ...userData.UserDetails,
                UserName: userName,
              },
            };
          });
        }

        setIsSaveClicked(false);
      }
    }
  }, [
    isEditEnabled,
    isErrorModal,
    isSaveClicked,
    updateAttributeOfUser,
    userData,
    userName,
  ]);

  const checkRegisteredNos = useCallback(async (attr, attrValue) => {
    try {
      const param = {
        attribute: attr,
        attribute_value: attrValue,
        action: "GetUserAttribute",
      };
      const resp = await getUserAuthenticationHookDetails(param);
      if (resp.data && resp.data.Users.length > 0) {
        const attribute = resp.data.Users[0].Attributes.find(
          (elem) => elem.Name === attr
        );
        return attribute ? true : false;
      } else {
        return false;
      }
    } catch (err) {}
  }, []);

  const handleMobile = useCallback(
    async (phone) => {
      let final_phone = phone;

      if (phone.length > 0) final_phone = "+" + phone;
      else final_phone = phone;

      setValidation({
        ...validation,
        phoneUsed: false,
      });

      if (
        final_phone.length < 1 ||
        final_phone.length === 0 ||
        final_phone === userData?.UserDetails?.PhoneNumber
      ) {
        setVerifyDisable(true);
      } else {
        setVerifyDisable(false);
      }

      // Debouncing Implemented
      if (debounceTimeout) {
        clearTimeout(debounceTimeout);
      }

      const newDebounceTimeout = setTimeout(async () => {
        const checkUserPhoneResp = await checkRegisteredNos(
          "phone_number",
          final_phone
        );

        if (checkUserPhoneResp) {
          setValidation((validation) => ({ ...validation, phoneUsed: true }));
        }
      }, 1000);

      setDebounceTimeout(newDebounceTimeout);
      setUserMobNo(final_phone);
    },
    [checkRegisteredNos, debounceTimeout, userData, validation]
  );

  // To Cleanup the debounce timeout
  useEffect(() => {
    return () => {
      if (debounceTimeout) {
        clearTimeout(debounceTimeout);
      }
    };
  }, [debounceTimeout]);

  const generateOTP = useCallback(async () => {
    try {
      const queryParam = {
        attribute: "phone_number",
        isChangePhoneNumber: !(
          userMobNo === userData?.UserDetails?.PhoneNumber
        ),
        cognitoId:
          userCognitoData?.signInUserSession?.accessToken?.payload?.username,
        value: userMobNo,
      };

      const response = await sendOTP(queryParam);
    } catch (err) {
      setIsErrorModal(false);
    }
  }, [userMobNo, userCognitoData, userData]);

  const validateOTP = useCallback(async () => {
    try {
      const queryParam = {
        cognitoId:
          userCognitoData?.signInUserSession?.accessToken?.payload?.username,
        attributeName: "phone_number",
        attributeValue: userMobNo,
        otp: otpEntered,
        requestedUserId: userCognitoData.username,
      };

      const response = await updateUserAttribute(queryParam);
      setIsMobOTPModalActive(false);
    } catch (err) {
      if (err.code !== "ERR_BAD_REQUEST") {
        setIsErrorModal(true);
      } else {
        setOTPVerifyError(true);
        setOTPVerifyErrorMessage("Incorrect Mobile Security Code!");
        setIsErrorModal(false);
      }
    }
  }, [otpEntered, userCognitoData, userMobNo]);

  const handleOtpChange = (val) => {
    setOtpEntered(val);
    setOTPVerifyError(false);
  };

  // Function to convert Image into Bytes
  const imageToBytes = useCallback(async (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = (event) => {
        resolve(new Uint8Array(event.target.result));
      };

      reader.onerror = (error) => {
        reject(error);
      };
      reader.readAsArrayBuffer(file);
    });
  }, []);

  const checkFileFormat = useCallback(() => {
    if (file) {
      setSelectedFileFormat("");
      setErrorMsg("");
      let type = file?.name.split(".").pop().toLowerCase();
      setSelectedFileFormat(type);
    }
  }, [file]);

  const handleFileChange = useCallback(
    async (event) => {
      const selectedFile = event.target.files[0];

      if (selectedFile) {
        setFile(selectedFile);

        checkFileFormat();

        const fileReader = new FileReader();
        fileReader.onload = () => {
          setPreviewUrl(fileReader.result);

          const err1 = t("editProfile.unsupportedPicture");
          const fileSizeInMB = selectedFile?.size / (1024 * 1024);
          const allowedFileTypes = ["jpeg", "jpg", "png"];

          if (fileSizeInMB > 4) {
            setErrorMsg(err1);
            return;
          }

          if (
            !allowedFileTypes.includes(
              selectedFile?.name.split(".").pop().toLowerCase()
            )
          ) {
            setErrorMsg(err1);
            return;
          }

          setActionSelected("upload");
        };
        fileReader.readAsDataURL(selectedFile);
      }
    },
    [checkFileFormat, t]
  );

  // Function to convert Bytes into Base64
  const bytesToBase64 = useCallback((bytes) => {
    const blob = new Blob([bytes]);

    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        resolve(reader.result.split(",")[1]);
      };

      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  }, []);

  const handleUpload = useCallback(async () => {
    try {
      if (!file) {
        return;
      }

      const selectedFileFormat = file?.name.split(".").pop().toLowerCase();
      const base64String = await imageToBytes(file).then((bytes) =>
        bytesToBase64(bytes)
      );

      const formData = new FormData();
      formData.append("profileImage", file);

      const queryParam = {
        cognitoId:
          userCognitoData?.signInUserSession?.accessToken?.payload?.username,
        fileFormat: selectedFileFormat,
        profileImage: base64String,
        requestedUserId: userCognitoData.username,
      };

      const response = await setUserProfileImage(queryParam);

      if (response.status === 200) {
        setIsPicUploadSuccess(true);
        getUserProfileDetails();
        setIsChangePicModalActive(false);
      }
    } catch (error) {
      if (error) {
        let err2 = t("editProfile.updateError");
        setErrorMsg(err2);
      }
    }
  }, [
    file,
    getUserProfileDetails,
    userCognitoData,
    bytesToBase64,
    imageToBytes,
    t,
  ]);

  const handleRemovePicture = useCallback(async () => {
    try {
      const queryParam = {
        cognitoId:
          userCognitoData?.signInUserSession?.accessToken?.payload?.username,
        requestedUserId: userCognitoData.username,
      };

      const response = await deleteUserProfileImage(queryParam);

      if (response.status === 200) {
        getUserProfileDetails();
        setIsChangePicModalActive(false);
      }
    } catch (error) {
      if (error) {
        let err2 = t("editProfile.updateError");
        setErrorMsg(err2);
      }
    }
  }, [getUserProfileDetails, userCognitoData, t]);

  useEffect(() => {
    if (isChangePicModalActive || isMobOTPModalActive) {
      hideEditProfileModal(true);
    } else {
      hideEditProfileModal(false);
    }
  }, [
    getUserProfileDetails,
    hideEditProfileModal,
    isChangePicModalActive,
    isMobOTPModalActive,
  ]);

  const handleSaveBtn = useCallback(() => {
    if (actionSelected === "remove") {
      handleRemovePicture();
    } else if (actionSelected === "upload") {
      handleUpload();
    }
  }, [actionSelected, handleRemovePicture, handleUpload]);

  return (
    <div className="edit-profile-container">
      {userData && (
        <div>
          {/* Header Section Starts */}
          <div className="header-section">
            <div className="heading">
              {t("UserPref.navOptions.editProfile")}
            </div>
            <div className="profile-img-section row-conatiner">
              <div className="left-sec">
                <div className="img">
                  {userData?.profileImage ? (
                    <img
                      src={userData?.profileImage}
                      className="img-fluid"
                      alt="user-profile"
                    />
                  ) : (
                    <img
                      src={require("../../assets/images/no-profile-img.png")}
                      className="img-fluid"
                      alt="user-profile"
                    />
                  )}
                </div>
                <div>
                  <label
                    className="user-name truncate-text"
                    title={userData?.UserDetails?.UserName || ""}
                  >
                    {userData?.UserDetails?.UserName || ""}
                  </label>
                  <p className="text user-role">
                    {userData?.UserDetails?.UserRole || ""}
                  </p>
                </div>
              </div>
              <div className="right-sec">
                <button
                  className="roundBtn linkColor camera"
                  onClick={() => {
                    setIsChangePicModalActive(true);
                  }}
                >
                  {Icons.cameraIcon}
                  <span id="changePic">{t("editProfile.changePic")}</span>
                </button>
              </div>
            </div>
          </div>
          {/* Header Section Ends */}

          {/* Body Section Starts */}
          <div className="body-container">
            {userData && (
              <>
                <div className="d-flex justify-content-between gap-150">
                  {/* Left Section starts */}
                  <div className="row-container">
                    <div className="name-heading margin-btm">
                      <label className="full-name-title">
                        {t("editProfile.fullName")}
                      </label>
                      {isEditEnabled ? (
                        <label
                          className={
                            (errorMsg || !userName.trim()) ? "action-label disabled" : "action-label"
                          }
                          id="saveNameBtn"
                          onClick={() => {
                            if (userName.trim()) {
                              userNameErrorCheck();
                            }
                          }}
                          disabled={errorMsg || !userName.trim()}
                        >
                          {t("editProfile.save")}
                        </label>
                      ) : (
                        <label
                          className="action-label"
                          id="editNameBtn"
                          onClick={() => {
                            setIsEditEnabled(true);
                            setIsSaveClicked(false);
                            setIsMobileNoEditEnabled(false);
                            setUserMobNo(userData?.UserDetails?.PhoneNumber);
                          }}
                        >
                          {t("editProfile.edit")}
                        </label>
                      )}
                    </div>
                    <div className="margin-btm5">
                      <div className="editContainer">
                        <input
                          type="text"
                          className="form-control"
                          value={userName}
                          onChange={handleUserNameEdit}
                          id="username"
                          placeholder=""
                          aria-label="Username"
                          name="username"
                          disabled={!isEditEnabled}
                          maxLength="64"
                        />
                        {isEditEnabled ? (
                          <span
                            className="closeIcon"
                            onClick={() => {
                              setIsEditEnabled(false);
                              setUserName(userData?.UserDetails?.UserName);
                              setErrorMsg("");
                            }}
                          >
                            {Icons.closeCircleIcon}
                          </span>
                        ) : null}
                      </div>
                      {errorMsg ? (
                        <span className="errorMsgText validationError nameMobError">
                          {errorMsg}
                        </span>
                      ) : null}
                    </div>
                    <div className="email-section margin-btm">
                      {Icons.userPref.emailIcon}
                      <div>
                        <label className="email-title">
                          {t("editProfile.email")}
                        </label>
                        <p className="text">
                          {userData?.UserDetails?.Email || ""}
                        </p>
                      </div>
                    </div>
                  </div>
                  {/* Left Section ends */}
                  {/* Right Section starts */}
                  <div className="row-container">
                    <div className="mobile-heading margin-btm">
                      <label className="mobile-title">
                        {t("editProfile.mobileText")}
                      </label>
                      {isMobileNoEditEnabled ? (
                        <label
                          className="action-label"
                          id="mobileVerifyBtn"
                          onClick={() => {
                            if (!verifyDisable) {
                              setIsMobileNoEditEnabled(false);
                              setIsMobOTPModalActive(true);
                              generateOTP();
                            }
                          }}
                        >
                          {t("editProfile.verify")}
                        </label>
                      ) : (
                        <label
                          className="action-label"
                          id="editMobBtn"
                          onClick={() => {
                            setIsMobileNoEditEnabled(true);
                            setIsEditEnabled(false);
                            setUserName(userData?.UserDetails?.UserName);
                            setErrorMsg("");
                            setValidation({
                              ...validation,
                              mobileEmpty: false,
                              mobileInvalid: false,
                              phoneUsed: false,
                            });
                          }}
                        >
                          {t("editProfile.edit")}
                        </label>
                      )}
                    </div>
                    <div className="margin-btm5">
                      {isMobileNoEditEnabled ? (
                        <div className="fieldset">
                          <div className="editContainer">
                            <div className="input-group mobilePlaceholder">
                              <PhoneInput
                                country={"us"}
                                placeholder="Enter mobile number"
                                onChange={handleMobile}
                                value={userMobNo}
                                className="phone-input"
                              />
                            </div>
                            {isMobileNoEditEnabled ? (
                              <span
                                className="closeIcon mobileNo"
                                id="mobileNoCloseIcon"
                                onClick={() => {
                                  setIsMobileNoEditEnabled(false);
                                  setUserMobNo(
                                    userData?.UserDetails?.PhoneNumber
                                  );
                                }}
                              >
                                {Icons.closeCircleIcon}
                              </span>
                            ) : null}
                          </div>
                          {validation.phoneUsed && (
                            <span
                              id="mobile_error2"
                              className="validationError nameMobError"
                            >
                              {t("editProfile.noAlreadyRegistered")}
                            </span>
                          )}
                        </div>
                      ) : (
                        <input
                          type="text"
                          className="form-control"
                          value={userMobNo}
                          id="userno"
                          placeholder=""
                          aria-label="Userno"
                          name="userno"
                          disabled={true}
                          maxLength="64"
                        />
                      )}
                    </div>
                    <div className="institute-section margin-btm">
                      {Icons.userPref.emailIcon}
                      <div>
                        <label className="institution-title">
                          {t("editProfile.institution")}
                        </label>
                        <p className="text">
                          {userData?.TenantDetails?.TenantName || ""}
                        </p>
                      </div>
                    </div>
                  </div>
                  {/* Right Section ends */}
                </div>
                {/* Sites Section starts */}
                <div className="sites-section row-conatiner">
                  <label>{t("editProfile.siteScope")}</label>
                  <div className="site-container">
                  {userData?.SiteDetails.map((site, i) => {
                      return (
                        site.HasAccess === "True" ? (
                          <div
                            className="site gap-2"
                            key={`${site?.SiteName} ${i + 1}`}
                          >
                            {Icons.userPref.siteIcon}
                            <label>{site?.SiteName}</label>
                          </div>
                        ) : <></>
                      );
                    })}
                  </div>
                </div>
                {/* Sites Section ends */}
              </>
            )}
          </div>
          {/* Body Section Ends */}
        </div>
      )}
      {!isLoading && err && (
        <>
          <div className="header-section no-backgraoud">
            <div className="heading">
              {t("UserPref.navOptions.editProfile")}
            </div>
          </div>
          <div className="empty-screen-container">
            <EmptyScreen
              img={Icons.userPref.noData}
              small={true}
              title={t("UserPref.noDataTitle")}
              desc={t("UserPref.noDataDescription")}
            />
          </div>
        </>
      )}
      {isLoading && <LoadingSpinner />}

      {isMobOTPModalActive && (
        <Modal
          isOpen={isMobOTPModalActive}
          onCloseModal={() => setIsMobOTPModalActive(false)}
          className="smallModal"
        >
          <div className="container-fluid flex-fill modalContainer">
            <div className="d-flex flex-row text-center align-items-center headingModalContainer">
              <button
                onClick={() => {
                  setIsMobOTPModalActive(false);
                  setOtpEntered("");
                }}
                id="wrongnumber"
                className="linkBtn d-flex flex-row text-center align-items-center"
              >
                <span id="leftArrowIcon">{Icons.leftArrowIcon}</span>
                <span
                  id="backButton"
                  onClick={() => {
                    setOtpEntered("");
                    setResendOTP(false);
                    setOTPVerifyErrorMessage("");
                    setUserMobNo(userData?.UserDetails?.PhoneNumber);
                  }}
                >
                  Back
                </span>
              </button>

              <div className="d-flex flex-column verifyMobileTextDiv">
                <span id="verifyText">{t("editProfile.verifyMobNo")}</span>

                {otpVerifyError && (
                  <span
                    id="code_error"
                    className="fs-7 mb-0 validationError text-center"
                  >
                    {otpVerifyErrorMessage}
                  </span>
                )}
              </div>

              <span></span>
            </div>
            <div className="fieldset d-flex flex-column text-center justify-content-center mb-4 align-items-center">
              <p className="fs-7" id="code-sent-text">
                {t("editProfile.sentSecurityCode", {
                  mobNo: `${userMobNo}`,
                })}
              </p>
            </div>
            <div className="fieldset d-flex flex-column justify-content-center align-items-center mb-4 otpInputWrapper">
              <OtpInput
                value={otpEntered}
                className="otpInput"
                focusStyle={{
                  border: "1px solid #CFD3DB",
                  outline: "none",
                }}
                onChange={(val) => {
                  handleOtpChange(val);
                }}
                separator={<span style={{ width: "8px" }}> </span>}
                shouldAutoFocus={true}
                numInputs={6}
                isInputNum={true}
                isDisabled={false}
              />
            </div>
            <div className="fieldset d-flex flex-row justify-content-between align-items-center mb-4 resendCodeDiv">
              <button
                onClick={() => {
                  generateOTP();
                  setResendOTP(true);
                  setOTPVerifyErrorMessage("");
                }}
                id="resendcode"
                name="resendcode"
                className="linkBtn"
              >
                {t("editProfile.resendCode")}
              </button>
            </div>
            {/* {resendOTP && (
              <div className="fieldset mb-4">
                <p
                  className="fs-7 mb-0 successMsg text-center"
                  id="code-resent-text"
                >
                  Security Code resent to {userMobNo}
                </p>
              </div>
            )} */}
            <div className="fieldset mb-3">
              <button
                type="submit"
                id="next"
                disabled={!(otpEntered?.length === 6)}
                className={
                  otpEntered?.length === 6 ? "submitBtn" : "submitBtn dark"
                }
                onClick={() => {
                  validateOTP();
                }}
              >
                <span
                  onClick={() => {
                    setOTPVerifyErrorMessage("");
                    setResendOTP(false);
                  }}
                >
                  {t("editProfile.verify")}
                </span>
              </button>
            </div>
          </div>

          <ModalComponent
            idName="saveErrorModal"
            isOpen={isErrorModal}
            onCloseModal={() => setIsErrorModal(false)}
            descriptionText={t("editProfile.updateError")}
          ></ModalComponent>
        </Modal>
      )}

      {/* Modal Section starts */}
      <Modal
        idName="changePic"
        isOpen={isChangePicModalActive}
        onCloseModal={() => setIsChangePicModalActive(false)}
        className="changePicModal"
      >
        <div className="header">
          <span className="heading">
            {t("editProfile.changeProfilePicture")}
          </span>

          {errorMsg ? <span className="errorMsgText">{errorMsg}</span> : null}
        </div>

        <div className="fieldset d-flex flex-row justify-content-between align-items-center body">
          <div className="btnContiner fieldset d-flex flex-column align-items-center btnContiner">
            <button className="roundBtn uploadBtn">
              <label id="uploadContainer">
                <span id="uploadIcon">{Icons.uploadIcon}</span>
                <span id="uploadText">{t("editProfile.uploadPicture")}</span>
                <input
                  type="file"
                  accept="image/*"
                  style={{ display: "none" }}
                  onChange={handleFileChange}
                />
              </label>
            </button>

            <div
              className="d-flex flex-row justify-content-between align-items-center deleteBtn"
              onClick={() => {
                setErrorMsg("");
                if (userData?.profileImage || previewUrl) {
                  setPreviewUrl(null);
                  setActionSelected("remove");
                }
              }}
            >
              <span
                className={
                  !previewUrl && !userData?.profileImage
                    ? "removePictureDisabled"
                    : ""
                }
                id="removeIcon"
              >
                {Icons.deleteJob}
              </span>
              <span
                className={
                  !previewUrl && !userData?.profileImage
                    ? "removePictureDisabled"
                    : ""
                }
                id="removeText"
              >
                {t("editProfile.removePicture")}
              </span>
            </div>
          </div>
          <div className="img newProfileImg">
            {previewUrl ? (
              <img
                src={previewUrl}
                alt="Preview"
                style={{ maxWidth: "100%", maxHeight: "200px" }}
              />
            ) : (
              <>
                {userData?.profileImage && actionSelected === "" ? (
                  <img
                    src={userData?.profileImage}
                    className="img-fluid"
                    alt="user-profile"
                  />
                ) : (
                  <img
                    src={require("../../assets/images/no-profile-img.png")}
                    className="img-fluid"
                    alt="user-profile"
                  />
                )}
              </>
            )}
          </div>
        </div>

        <div className="foot">
          <button
            type="submit"
            id="cancel"
            className="submitBtn dark"
            onClick={() => {}}
          >
            <span
              onClick={() => {
                setIsChangePicModalActive(false);
                getUserProfileDetails();
                setPreviewUrl(null);
                setActionSelected("");
                setErrorMsg("");
              }}
            >
              {t("editProfile.cancel")}
            </span>
          </button>

          <button
            type="submit"
            id="save"
            className="roundBtn primary"
            onClick={() => {}}
            disabled={
              !actionSelected ||
              errorMsg ||
              userData?.profileImage === previewUrl
            }
          >
            <span
              onClick={() => {
                handleSaveBtn();
                setErrorMsg("");
              }}
            >
              {t("editProfile.save")}
            </span>
          </button>
        </div>
      </Modal>
      {/* Modal Section Ends */}
    </div>
  );
};

export default EditProfile;
